/**
 * 앱 안 레이 위젯이 부르는 가이드 도우미 함수 `lay-docs-chat` (Rhoonart CS System, 소스 docs-site/server/lay-docs-chat/).
 * docs-site/src/lib/layChat.js 의 askLay 와 같은 계약 — 서버는 사용자가 볼 수 있는 가이드 문서만 근거로 답하고,
 * 역할 판정은 Authorization 토큰으로 서버가 한다. 클라이언트는 질문·대화 id·화면 맥락(context)만 보낸다.
 * v9(2026-09-09)부터 `context`(화면 경로 + 지목한 요소 문구, 600자)를 받는다.
 * v11(2026-09-10) 「운영팀에 물어볼까요?」: `escalate_mode: "ask"` 를 보내므로 서버는 가이드에 없는 답이어도 바로 에스컬레이션하지
 * 않고 `escalation: "offer"` 로 돌려준다. 사용자가 「예」를 누르면 escalateLay() 가 { action: "escalate" } 로 다시 불러 그때 넣는다.
 */
import { supabase } from "./supabaseClient";
import { tt } from "../i18n/tt";

export const LAY_MAX_MESSAGE = 500;
export const LAY_CONTEXT_MAX = 1200;

/**
 * 화면 어디서든 레이 위젯을 열고 질문을 미리 채운다 — 상시 도움말(GuideHint)의 「레이에게 묻기」가 쓴다.
 * LayWidget 이 이 이벤트를 듣는다(`laeebly:lay:ask`, detail.question). 위젯이 안 떠 있는 화면이면 아무 일도 없다.
 */
export const LAY_ASK_EVENT = "laeebly:lay:ask";
export function askLayFromScreen(question) {
  if (typeof window === "undefined") return;
  const q = String(question || "").trim().slice(0, LAY_MAX_MESSAGE);
  if (!q) return;
  try { window.dispatchEvent(new CustomEvent(LAY_ASK_EVENT, { detail: { question: q } })); } catch { /* 무시 */ }
}
const URL = process.env.REACT_APP_LAY_CHAT_URL || "https://aajtilnicgqywpmuuxtr.supabase.co/functions/v1/lay-docs-chat";
const GENERIC_ERROR = "지금 답변 생성이 어려워요. 잠시 후 다시 시도해 주세요.";

export class LayError extends Error {
  constructor(status, content, retryAfter) { super(content); this.name = "LayError"; this.status = status; this.content = content; this.retryAfter = retryAfter; }
}

/** 역할 → 추천 질문 범위. docs-site AuthContext.audiencesFromRole · lay-docs-chat scopeFromRole 과 같은 규칙(서버가 최종 판정). */
export function scopeFromRole(role) {
  const r = role || "";
  if (r.includes("Admin") || r.includes("Master") || r.includes("Manager")) return "all";
  // 파트너(비어드민)는 파트너 문서만 — 크리에이터·권리사 겸직이어도 파트너 범위 (기획 partner-program-plan.md §10, 2026-09-10)
  if (r.includes("Partner")) return "partner";
  const rights = r.includes("Copyrights Holder");
  const creator = r.includes("Creator");
  if (rights && creator) return "all";
  if (rights) return "rights";
  return "creator";
}

async function post(payload) {
  const { data: { session } = {} } = await supabase.auth.getSession();
  const headers = { "Content-Type": "application/json" };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  let res;
  try {
    res = await fetch(URL, { method: "POST", headers, body: JSON.stringify(payload) });
  } catch (e) {
    throw new LayError(0, GENERIC_ERROR);
  }
  let data = {};
  try { data = await res.json(); } catch (e) { data = {}; }
  if (res.status === 429) throw new LayError(429, data.content || "질문이 잠시 많이 몰렸어요. 조금 뒤에 다시 시도해 주세요.", 60);
  if (res.status === 403) throw new LayError(403, data.error === "not_conversation_owner" ? "이 대화는 지금 세션의 것이 아니에요. 새 대화로 다시 물어봐 주세요." : "이 주소에서는 레이를 쓸 수 없어요.");
  if (!res.ok) throw new LayError(res.status, data.content || GENERIC_ERROR);
  return data;
}

/** 서버 응답의 escalation 을 정규화한다. v11 이전 서버(escalation 없음)는 escalated 면 이미 넣은 것("sent"). */
export function normalizeEscalation(data) {
  if (data && (data.escalation === "offer" || data.escalation === "sent")) return data.escalation;
  return data && data.escalated ? "sent" : "none";
}

/** 위젯에 저장된 레이 답의 에스컬레이션 상태: "none" | "offer"(되묻는 중) | "sent" | "declined". v11 이전 저장본(escalation 없음)은 이미 넘어간 것. */
export function escalationState(m) {
  if (!m || m.role === "user" || !m.escalated) return "none";
  return m.escalation === "offer" || m.escalation === "sent" || m.escalation === "declined" ? m.escalation : "sent";
}

/** 성공: { content, sources[], conversationId, messageId, escalated, escalation: "none"|"offer"|"sent", scope } · 실패: throw LayError */
// me(2026-09-13): false 면 서버가 내 상황(<me>)을 읽지 않는다. 생략·true 면 로그인 사용자에 한해 서버가 읽는다(lay-docs-chat v14).
// topicSlugs(2026-10-28): 문의 길잡이에서 고른 세부의 가이드 slug — 서버가 위키 문서 고르기에 늘 포함한다(lay-docs-chat topic_slugs).
export async function askLay({ message, conversationId, context, honeypot, me, topicSlugs }) {
  const data = await post({
    message: String(message || "").slice(0, LAY_MAX_MESSAGE),
    ...(conversationId ? { conversation_id: conversationId } : {}),
    ...(context ? { context: String(context).slice(0, LAY_CONTEXT_MAX) } : {}),
    escalate_mode: "ask",
    ...(me === false ? { me: false } : {}),
    ...(Array.isArray(topicSlugs) && topicSlugs.length ? { topic_slugs: topicSlugs.slice(0, 4) } : {}),
    _hp: honeypot || "",
  });
  return {
    content: String(data.content || ""),
    sources: Array.isArray(data.sources) ? data.sources : [],
    conversationId: data.conversation_id || conversationId || null,
    messageId: data.message_id || null,
    escalated: Boolean(data.escalated),
    escalation: normalizeEscalation(data),
    scope: data.scope || null,
  };
}

/** 「운영팀에 물어볼까요?」 → 예. 그 답(messageId)의 질문을 lay_escalations 에 넣는다. 성공: { escalationId } · 실패: throw LayError */
export async function escalateLay({ conversationId, messageId }) {
  if (!conversationId) throw new LayError(400, "이 대화는 아직 저장되지 않아 전달할 수 없어요. 다시 한 번 물어봐 주세요.");
  const data = await post({ action: "escalate", conversation_id: conversationId, ...(messageId ? { message_id: messageId } : {}) });
  if (!data.ok) throw new LayError(500, "전달에 실패했어요. 다시 시도해 주세요.");
  return { escalationId: data.escalation_id || null, duplicate: Boolean(data.duplicate) };
}

/** 지목한 요소를 서버에 보낼 한 줄로 요약한다 — 태그·라벨·보이는 문구(300자). 값 속성(value)은 넣지 않는다. */
export function describeElement(el, pathname) {
  if (!el) return pathname ? tt("a.32469047", "경로 {{v0}}", { v0: pathname }) : "";
  const label = el.getAttribute?.("data-lay") || el.getAttribute?.("aria-label") || el.getAttribute?.("alt") || el.getAttribute?.("title") || "";
  const heading = el.closest?.("section, article, [role='region'], main")?.querySelector?.("h1, h2, h3, h4")?.innerText?.trim().slice(0, 60) || "";
  const text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 300);
  return [pathname ? `경로 ${pathname}` : null, heading ? `영역 「${heading}」` : null, `요소 <${el.tagName.toLowerCase()}>${label ? ` ${label}` : ""}`, text ? `문구 「${text}」` : null].filter(Boolean).join(" · ");
}
